import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock } from "lucide-react";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { ArticleBody } from "@/components/site/ArticleBody";
import { ArticleCard } from "@/components/site/ArticleCard";
import { getBlogPostLocalized } from "@/lib/blog-localized.functions";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { AuthorByline } from "@/components/site/AuthorByline";
import { ReadingProgress } from "@/components/site/wow/ReadingProgress";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { useReadGoal } from "@/hooks/useReadGoal";
import { article, breadcrumbs, speakable, qaPage, person, aeoGeo } from "@/lib/seo/schema";
import { abs , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

// Reject Swedish slugs on the English route (F-005). The CMS would otherwise
// fall back to Swedish content under an /en/ URL, producing a broken hreflang.
const SV_SLUG_HINTS = /^(vad-|hur-|varfor-|nar-|vilken-|vilka-|kunskap-)/i;
const SV_CHARS = /[åäöÅÄÖ]/;

export const Route = createFileRoute("/en_/knowledge/$slug")({
  loader: async ({ params, context }) => {
    // Svensk slug under /en/ — skicka vidare till den svenska artikeln i
    // stället för 404. Google har hittat flera sådana URL:er.
    if (SV_SLUG_HINTS.test(params.slug) || SV_CHARS.test(params.slug)) {
      throw redirect({ to: "/kunskap/$slug", params: { slug: params.slug }, statusCode: 301 });
    }
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["blog-post", "en", params.slug],
      queryFn: () =>
        getBlogPostLocalized({ data: { slug: params.slug, locale: "en" } }),
    });
    if (!data.post) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const title = post?.seo_title || post?.title || "Article — Fenny";
    const description =
      post?.seo_description || post?.excerpt || "Knowledge from Fenny.";
    const ogImage = SOCIAL_IMAGE_URL;
    return {
      meta: [
        { title: `${title} | Fenny` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://fenny.se/en/knowledge/${params.slug}` },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: `https://fenny.se/en/knowledge/${params.slug}` }],
      scripts: post
        ? [
            { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: `/en/knowledge/${params.slug}`})) },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                article({
                  headline: post.title,
                  description: post.excerpt,
                  path: `/en/knowledge/${params.slug}`,
                  image: ogImage,
                  datePublished: post.published_at,
                  dateModified: post.updated_at ?? post.published_at,
                  authorName: post.author_name,
                  authorRole: post.author_role,
                  section: post.category,
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbs([
                  { name: "Home", path: "/en" },
                  { name: "Knowledge", path: "/en/knowledge" },
                  { name: post.title, path: `/en/knowledge/${params.slug}` },
                ]),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                speakable(["h1", "[data-speakable]", ".tldr"]),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                person({
                  name: post.author_name,
                  jobTitle: post.author_role,
                  path: `/en/knowledge/${params.slug}`,
                }),
              ),
            },
            ...(post.title.toLowerCase().startsWith("what ") ||
            post.title.toLowerCase().startsWith("how ") ||
            post.title.toLowerCase().includes("cost")
              ? [
                  {
                    type: "application/ld+json" as const,
                    children: JSON.stringify(
                      qaPage({
                        question: post.title,
                        answer: post.excerpt,
                        path: `/en/knowledge/${params.slug}`,
                        authorName: post.author_name,
                      }),
                    ),
                  },
                ]
              : []),
          ]
        : [],
    };
  },
  component: ArticlePage,
  notFoundComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Article not found</h1>
      <p className="mt-3 text-muted-foreground">
        This article may have been removed or moved.
      </p>
      <div className="mt-6">
        <Button to="/en/knowledge" variant="mint" arrow>
          To the knowledge library
        </Button>
      </div>
    </div>
  ),
  errorComponent: () => (
    <div className="container-wide pt-40 pb-20">
      <h1 className="text-3xl font-semibold">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">
        The article could not be loaded. Please reload the page.
      </p>
    </div>
  ),
});

function ArticlePage() {
  const { slug } = Route.useParams();
  const fetchPost = useServerFn(getBlogPostLocalized);
  const { data } = useSuspenseQuery({
    queryKey: ["blog-post", "en", slug],
    queryFn: () => fetchPost({ data: { slug, locale: "en" } }),
  });
  const post = data.post!;
  const related = data.related;
  const date = new Date(post.published_at).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useReadGoal("knowledge_read_75", 0.75, { slug });

  return (
    <article>
      <ReadingProgress tone="mint" />
      <section className="pt-36 pb-10 md:pt-44">
        <div className="container-wide max-w-3xl">
          <Link
            to="/en/knowledge"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-mint"
          >
            <ArrowLeft className="h-4 w-4" /> Knowledge
          </Link>
          <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-mint">
            <span>{post.category}</span>
            <span aria-hidden className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {post.read_minutes} min read
            </span>
          </div>
          <h1 className="mt-5 text-balance text-4xl font-semibold leading-[1.05] text-butter md:text-6xl">
            {post.title}
          </h1>
          <p className="mt-6 text-pretty text-lg text-foreground/85 md:text-xl">
            {post.excerpt}
          </p>
          <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6 text-sm text-muted-foreground">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-mint/15 text-mint">
              ✦
            </div>
            <div>
              <div className="font-medium text-foreground">
                {post.author_name}
              </div>
              <div className="text-xs">
                {post.author_role} · {date}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Section className="py-8 md:py-12">
        <div className="mx-auto max-w-3xl">
          <ArticleBody body={post.body} />
          <div className="mt-12 border-t border-white/10 pt-8">
            <AuthorByline date={post.published_at} reviewedDate={post.updated_at ?? undefined} />
          </div>
        </div>
      </Section>

      <SignatureCta
        eyebrow="Want us to make this for you?"
        title="Pulse turns ideas into film. Every week."
        body="Fenny Pulse is a video subscription for marketing teams that want ongoing, high-quality video content without holding the baton themselves."
        tone="mint"
      />

      <Section className="bg-surface/40">
        <div className="mx-auto max-w-3xl">
          <LeadMagnetForm variant="inline" location={`en_article_${slug}`} />
        </div>
      </Section>

      {related.length > 0 && (
        <Section>
          <div className="mb-10 text-xs uppercase tracking-[0.18em] text-mint">
            Read more
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {related.map((p) => (
              <ArticleCard key={p.slug} post={p} />
            ))}
          </div>
        </Section>
      )}
    </article>
  );
}
